import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import { siteConfig } from '@/mocks/siteConfig';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return <NotFound />;
  }

  const related = getRelatedComics(comic);
  const categoryInfo = categories.find((c) => c.slug === comic.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.updatedAt,
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
      url: siteConfig.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.url}/article/${comic.id}`,
    },
  };

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryInfo?.name || '漫画', href: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        {/* Comic Detail */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Cover */}
            <div className="flex-shrink-0 w-full md:w-[280px]">
              <div className="aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩漫画网`}
                  title={`${comic.title} - 韩国漫画大全`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-600 mb-5 leading-relaxed">
                {comic.description}
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-5">
                <div className="flex items-center gap-1.5 text-sm">
                  <i className="ri-user-line text-foreground-400"></i>
                  <span className="text-foreground-700">{comic.author}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="text-foreground-700 font-semibold">{comic.rating}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm">
                  <i className="ri-eye-line text-foreground-400"></i>
                  <span className="text-foreground-700">{comic.views}</span>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Action */}
              <div className="flex items-center gap-3">
                <a
                  href={`/article/${comic.id}/read`}
                  className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap flex items-center gap-2 transition-colors"
                >
                  <i className="ri-play-line"></i> 开始阅读
                </a>
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors"
                  aria-label="收藏"
                >
                  <i className="ri-heart-line text-lg"></i>
                </button>
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors"
                  aria-label="分享"
                >
                  <i className="ri-share-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 border-t border-background-200/70">
          <h2 className="text-lg font-bold font-heading text-foreground-950 mb-5">
            章节列表
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {comic.chapters.map((chapter) => (
              <a
                key={chapter.id}
                href={`/article/${comic.id}/read/${chapter.number}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex items-center justify-between p-3.5 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-background-100 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 group-hover:bg-primary-100 transition-colors">
                    <i className="ri-file-text-line text-sm text-foreground-500 group-hover:text-primary-500"></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-950">{chapter.title}</span>
                </div>
                <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
              </a>
            ))}
          </div>
        </section>

        {/* Related Comics */}
        {related.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 border-t border-background-200/70">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold font-heading text-foreground-950">
                相关推荐
              </h2>
              <Link
                to={`/category/${comic.category}`}
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
              >
                更多{categories.find(c => c.slug === comic.category)?.name}漫画 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((r) => (
                <ComicCard key={r.id} comic={r} />
              ))}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}